import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, ShieldCheck, ShieldOff, Loader2, SmartphoneNfc, KeyRound, Eye, EyeOff, ArrowLeft, PanelLeft } from 'lucide-react';
import { api, useAuth } from '@onvice/portal-shell-api';

import KalenderSubscribeCard from '../components/KalenderSubscribeCard';
import NotificationSettingsCard from '../components/NotificationSettingsCard';
import NotificationMandatorySlugsCard from '../components/NotificationMandatorySlugsCard';

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Passwort
  const [showPwChange, setShowPwChange] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [newPwConfirm, setNewPwConfirm] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMessage, setPwMessage] = useState('');

  // 2FA
  const [totpSetup, setTotpSetup] = useState<{ qrCode: string; secret: string } | null>(null);
  const [totpEnabled, setTotpEnabled] = useState<boolean | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);
  const [totpMessage, setTotpMessage] = useState('');
  const [showDisable2FA, setShowDisable2FA] = useState(false);
  const [disableCode, setDisableCode] = useState('');

  const load2FAStatus = async () => {
    setTotpLoading(true);
    try {
      const res = await api.get<{ success: boolean; data: { totpEnabled: boolean; qrCode?: string; secret?: string } }>('/auth/totp-setup');
      setTotpEnabled(res.data.totpEnabled);
      if (!res.data.totpEnabled) setTotpSetup({ qrCode: res.data.qrCode!, secret: res.data.secret! });
    } catch { setTotpMessage('Fehler beim Laden des 2FA-Status.'); }
    finally { setTotpLoading(false); }
  };

  const enableTotp = async () => {
    if (totpCode.length !== 6) return;
    setTotpLoading(true); setTotpMessage('');
    try {
      await api.post('/auth/totp-enable', { code: totpCode });
      setTotpEnabled(true); setTotpSetup(null); setTotpCode(''); setTotpMessage('2FA wurde aktiviert.');
    } catch (err: unknown) { setTotpMessage((err as Error).message || 'Ungültiger Code.'); }
    finally { setTotpLoading(false); }
  };

  const disableTotp = async () => {
    if (disableCode.length !== 6) return;
    setTotpLoading(true); setTotpMessage('');
    try {
      await api.post('/auth/totp-disable', { code: disableCode });
      setTotpEnabled(false); setTotpSetup(null); setDisableCode(''); setShowDisable2FA(false); setTotpMessage('2FA wurde deaktiviert.');
    } catch (err: unknown) { setTotpMessage((err as Error).message || 'Ungültiger Code.'); }
    finally { setTotpLoading(false); }
  };

  const changePw = async () => {
    setPwMessage('');
    if (!currentPw || !newPw) { setPwMessage('Bitte alle Felder ausfüllen.'); return; }
    if (newPw !== newPwConfirm) { setPwMessage('Neue Passwörter stimmen nicht überein.'); return; }
    if (newPw.length < 13) { setPwMessage('Mindestens 13 Zeichen erforderlich.'); return; }
    if (!/[a-z]/.test(newPw)) { setPwMessage('Mindestens ein Kleinbuchstabe erforderlich.'); return; }
    if (!/[A-Z]/.test(newPw)) { setPwMessage('Mindestens ein Grossbuchstabe erforderlich.'); return; }
    if (!/[0-9]/.test(newPw)) { setPwMessage('Mindestens eine Zahl erforderlich.'); return; }
    if (!/[^a-zA-Z0-9]/.test(newPw)) { setPwMessage('Mindestens ein Sonderzeichen erforderlich.'); return; }
    setPwLoading(true);
    try {
      await api.post('/auth/change-password', { currentPassword: currentPw, newPassword: newPw });
      setPwMessage('Passwort wurde geändert.');
      setCurrentPw(''); setNewPw(''); setNewPwConfirm(''); setShowPwChange(false);
    } catch (err: unknown) { setPwMessage((err as Error).message || 'Fehler.'); }
    finally { setPwLoading(false); }
  };

  const cardClass = "bg-slate-900 rounded-xl sm:rounded-lg p-5 sm:p-4 border border-slate-800 space-y-3";
  const btnOutline = "w-full h-12 sm:h-9 rounded-xl sm:rounded-lg border font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50";

  return (
    <div className="px-4 sm:px-8 py-8 max-w-xl mx-auto space-y-4">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-slate-200 transition-colors">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl sm:text-xl font-bold text-slate-100">Einstellungen</h1>
      </div>

      {/* User info */}
      <div className={cardClass}>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 sm:w-10 sm:h-10 rounded-full bg-brand-600 flex items-center justify-center text-primary-foreground font-bold text-xl sm:text-sm">
            {user?.firstName?.[0] || user?.email?.[0]?.toUpperCase() || '?'}
          </div>
          <div>
            <p className="text-slate-100 font-semibold text-lg sm:text-base">{user?.firstName} {user?.lastName}</p>
            <p className="text-slate-400 text-sm">{user?.email}</p>
            <span className="inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium bg-brand-500/20 text-brand-400">
              {user?.role === 'admin' ? 'Administrator' : 'Benutzer'}
            </span>
          </div>
        </div>
      </div>

      {/* Sidebar-Sichtbarkeit (Welle P3, Joerg 2026-07-06) */}
      <SidebarToggleCard cardClass={cardClass} />

      {/* „Wer ist online"-Sichtbarkeit (PWA-Offline-Plan P1.4/F6, 2026-09-09) */}
      <PresenceOptOutCard cardClass={cardClass} />

      {/* Passwort ändern */}
      <div className={cardClass}>
        <button onClick={() => { setShowPwChange(!showPwChange); setPwMessage(''); }} className="w-full flex items-center justify-between">
          <div className="flex items-center gap-2">
            <KeyRound size={18} className="text-brand-400" />
            <span className="text-slate-100 font-semibold text-sm">Passwort ändern</span>
          </div>
          <span className={`text-slate-400 transition-transform text-xs ${showPwChange ? 'rotate-90' : ''}`}>&#9654;</span>
        </button>

        {pwMessage && (
          <div className={`p-3 rounded-lg text-center text-sm ${pwMessage.includes('geändert') ? 'bg-brand-500/10 text-brand-400 border border-brand-500/30' : 'bg-red-500/10 text-red-400 border border-red-500/30'}`}>{pwMessage}</div>
        )}

        {showPwChange && (
          <div className="space-y-3">
            <div className="relative">
              <input type={showCurrentPw ? 'text' : 'password'} value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
                placeholder="Aktuelles Passwort"
                className="w-full h-12 sm:h-10 px-4 sm:px-3 pr-12 rounded-xl sm:rounded-lg bg-slate-800 border border-slate-700 text-slate-100 placeholder:text-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/50" />
              <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                {showCurrentPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="relative">
              <input type={showNewPw ? 'text' : 'password'} value={newPw} onChange={(e) => setNewPw(e.target.value)}
                placeholder="Neues Passwort"
                className="w-full h-12 sm:h-10 px-4 sm:px-3 pr-12 rounded-xl sm:rounded-lg bg-slate-800 border border-slate-700 text-slate-100 placeholder:text-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/50" />
              <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                {showNewPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <input type={showNewPw ? 'text' : 'password'} value={newPwConfirm} onChange={(e) => setNewPwConfirm(e.target.value)}
              placeholder="Neues Passwort bestätigen"
              className="w-full h-12 sm:h-10 px-4 sm:px-3 rounded-xl sm:rounded-lg bg-slate-800 border border-slate-700 text-slate-100 placeholder:text-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/50" />
            <p className="text-xs text-slate-500 px-1">Mind. 13 Zeichen, Gross-/Kleinbuchstaben, Zahl und Sonderzeichen</p>
            <button onClick={changePw} disabled={pwLoading || !currentPw || !newPw || !newPwConfirm}
              className="w-full h-10 sm:h-9 rounded-xl sm:rounded-lg bg-brand-600 hover:bg-brand-500 text-primary-foreground font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-colors">
              {pwLoading ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
              Passwort ändern
            </button>
          </div>
        )}
      </div>

      {/* 2FA */}
      <div className={cardClass}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-brand-400" />
            <span className="text-slate-100 font-semibold text-sm">Zwei-Faktor-Authentifizierung</span>
          </div>
          {totpEnabled === true && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 font-medium">Aktiv</span>
          )}
        </div>

        {totpMessage && (
          <div className={`p-3 rounded-lg text-center text-sm ${totpMessage.includes('aktiviert') ? 'bg-brand-500/10 text-brand-400 border border-brand-500/30' : totpMessage.includes('deaktiviert') ? 'bg-amber-500/10 text-amber-400 border border-amber-500/30' : 'bg-red-500/10 text-red-400 border border-red-500/30'}`}>{totpMessage}</div>
        )}

        {totpEnabled === null && !totpSetup && (
          <button onClick={load2FAStatus} disabled={totpLoading}
            className={`${btnOutline} border-slate-700 text-slate-400 hover:bg-slate-800 text-sm`}>
            {totpLoading ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
            2FA einrichten
          </button>
        )}

        {totpSetup && !totpEnabled && (
          <div className="space-y-3">
            <p className="text-slate-400 text-sm">Scanne den QR-Code mit deiner Authenticator-App.</p>
            <div className="flex justify-center p-4 bg-white rounded-xl"><img src={totpSetup.qrCode} alt="QR-Code" className="w-48 h-48" /></div>
            <div className="p-3 rounded-lg bg-slate-800 border border-slate-700">
              <p className="text-[10px] text-slate-500 uppercase font-semibold mb-1">Manueller Code</p>
              <p className="text-slate-200 font-mono text-sm break-all select-all">{totpSetup.secret}</p>
            </div>
            <input type="text" inputMode="numeric" maxLength={6} value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="w-full h-14 sm:h-10 px-4 rounded-xl sm:rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-center text-xl sm:text-lg font-mono tracking-[0.4em] placeholder:text-slate-500/30 focus:outline-none focus:ring-2 focus:ring-brand-500/50" />
            <button onClick={enableTotp} disabled={totpLoading || totpCode.length !== 6}
              className="w-full h-10 sm:h-9 rounded-xl sm:rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-colors">
              {totpLoading ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
              2FA aktivieren
            </button>
          </div>
        )}

        {totpEnabled === true && !showDisable2FA && (
          <button onClick={() => { setShowDisable2FA(true); setTotpMessage(''); setDisableCode(''); }} disabled={totpLoading}
            className={`${btnOutline} border-red-500 text-red-400 hover:bg-red-500/10 text-sm`}>
            <ShieldOff size={16} />
            2FA deaktivieren
          </button>
        )}

        {totpEnabled === true && showDisable2FA && (
          <div className="space-y-3">
            <p className="text-slate-400 text-sm">Gib deinen Authenticator-Code ein, um 2FA zu deaktivieren.</p>
            <input type="text" inputMode="numeric" maxLength={6} value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="w-full h-14 sm:h-10 px-4 rounded-xl sm:rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-center text-xl sm:text-lg font-mono tracking-[0.4em] placeholder:text-slate-500/30 focus:outline-none focus:ring-2 focus:ring-red-500/50" />
            <div className="flex gap-2">
              <button onClick={() => { setShowDisable2FA(false); setDisableCode(''); setTotpMessage(''); }}
                className="flex-1 h-10 sm:h-9 rounded-xl sm:rounded-lg border border-slate-700 text-slate-400 hover:bg-slate-800 text-sm font-semibold transition-colors">
                Abbrechen
              </button>
              <button onClick={disableTotp} disabled={totpLoading || disableCode.length !== 6}
                className="flex-1 h-10 sm:h-9 rounded-xl sm:rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-colors">
                {totpLoading ? <Loader2 size={16} className="animate-spin" /> : <ShieldOff size={16} />}
                Deaktivieren
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Benachrichtigungs-Einstellungen */}
      <NotificationSettingsCard />

      {/* Admin-only: Pflicht-Benachrichtigungen (Card blendet sich fuer Nicht-Admin selbst aus) */}
      <NotificationMandatorySlugsCard />

      {/* Kalender-Abos */}
      <KalenderSubscribeCard />

      {/* Alle Geräte abmelden */}
      <button
        onClick={async () => {
          if (!confirm('Alle Geräte abmelden?')) return;
          try { await api.post('/auth/logout-all'); } catch { /* ignore */ }
          logout();
        }}
        className={`${btnOutline} border-amber-500 text-amber-400 hover:bg-amber-500/10 text-sm`}>
        <SmartphoneNfc size={18} /> Alle Geräte abmelden
      </button>

      {/* Logout */}
      <button onClick={() => logout()}
        className={`${btnOutline} border-red-500 text-red-400 hover:bg-red-500/10 text-sm`}>
        <LogOut size={18} /> Abmelden
      </button>
    </div>
  );
}

interface SidebarCardProps { cardClass: string }

/**
 * Opt-out aus der „Wer ist online"-Anzeige (P1.4/F6). Blendet sich selbst aus,
 * wenn das Feature tenant-weit abgeschaltet ist.
 */
function PresenceOptOutCard({ cardClass }: SidebarCardProps) {
  const [enabled, setEnabled] = useState<boolean>(false);
  const [optedOut, setOptedOut] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get<{ success: boolean; data: { enabled: boolean; self_opted_out: boolean } }>('/portal/presence')
      .then((res) => {
        setEnabled(res.data.enabled);
        setOptedOut(res.data.self_opted_out);
      })
      .catch(() => {});
  }, []);

  if (!enabled) return null;

  async function toggle(next: boolean) {
    setSaving(true);
    try {
      await api.put('/portal/presence/optout', { opted_out: next });
      setOptedOut(next);
    } catch {
      // silent fail
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={cardClass}>
      <div className="flex items-center gap-2">
        {optedOut ? <EyeOff size={18} className="text-brand-400" /> : <Eye size={18} className="text-brand-400" />}
        <span className="text-slate-100 font-semibold text-sm">„Wer ist online"-Anzeige</span>
      </div>
      <p className="text-xs text-slate-400">
        Kollegen sehen, ob du gerade im Portal aktiv bist. Aktuell: <b>{optedOut ? 'ausgeblendet' : 'sichtbar'}</b>
      </p>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          disabled={saving}
          onClick={() => void toggle(false)}
          className={`h-10 rounded text-xs border ${!optedOut ? 'bg-brand-600 text-primary-foreground border-brand-600' : 'bg-slate-800 text-slate-300 border-slate-700'}`}
        >
          Sichtbar
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={() => void toggle(true)}
          className={`h-10 rounded text-xs border ${optedOut ? 'bg-brand-600 text-primary-foreground border-brand-600' : 'bg-slate-800 text-slate-300 border-slate-700'}`}
        >
          Nicht anzeigen
        </button>
      </div>
    </div>
  );
}

function SidebarToggleCard({ cardClass }: SidebarCardProps) {
  const [visible, setVisible] = useState<boolean | null>(null);
  const [tenantDefault, setTenantDefault] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get<{ success: boolean; data: { sidebar_show: boolean | null; sidebar_show_default: boolean } }>(
      '/portal/me/sidebar-prefs',
    )
      .then((res) => {
        setVisible(res.data.sidebar_show);
        setTenantDefault(res.data.sidebar_show_default);
      })
      .catch(() => {});
  }, []);

  async function set(v: boolean | null) {
    setSaving(true);
    try {
      await api.patch('/portal/me/sidebar-prefs', { sidebar_show: v });
      setVisible(v);
      // Sofortige Anwendung im Layout: LocalStorage + Reload
      const effective = v ?? tenantDefault;
      localStorage.setItem('onvice.sidebar.show', effective ? '1' : '0');
      window.location.reload();
    } catch {
      // silent fail
    } finally {
      setSaving(false);
    }
  }

  const effective = visible ?? tenantDefault;

  return (
    <div className={cardClass}>
      <div className="flex items-center gap-2">
        <PanelLeft size={18} className="text-brand-400" />
        <span className="text-slate-100 font-semibold text-sm">Sidebar (Navigations-Spalte)</span>
      </div>
      <p className="text-xs text-slate-400">
        Zeigt links eine Anwendungs-Navigation. Effektiv: <b>{effective ? 'sichtbar' : 'ausgeblendet'}</b>
        {visible === null && <span> (folgt Tenant-Default)</span>}
      </p>
      <div className="grid grid-cols-3 gap-2">
        <button
          type="button"
          disabled={saving}
          onClick={() => void set(true)}
          className={`h-10 rounded text-xs border ${visible === true ? 'bg-brand-600 text-primary-foreground border-brand-600' : 'bg-slate-800 text-slate-300 border-slate-700'}`}
        >
          Anzeigen
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={() => void set(false)}
          className={`h-10 rounded text-xs border ${visible === false ? 'bg-brand-600 text-primary-foreground border-brand-600' : 'bg-slate-800 text-slate-300 border-slate-700'}`}
        >
          Ausblenden
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={() => void set(null)}
          className={`h-10 rounded text-xs border ${visible === null ? 'bg-brand-600 text-primary-foreground border-brand-600' : 'bg-slate-800 text-slate-300 border-slate-700'}`}
        >
          Tenant-Default
        </button>
      </div>
    </div>
  );
}
